/**
 * AI Configuration Page
 *
 * T014: Tabbed page with Provider, Privacy & AI Controls, Usage Analytics
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Cpu, Shield, BarChart3 } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { ProviderTab } from './ProviderTab';
import { PrivacyControlsTab } from './PrivacyControlsTab';
import { UsageAnalyticsTab } from './UsageAnalyticsTab';

type TabId = 'provider' | 'privacy' | 'usage';

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'provider', label: 'Provider', icon: Cpu },
  { id: 'privacy', label: 'Privacy & AI Controls', icon: Shield },
  { id: 'usage', label: 'Usage Analytics', icon: BarChart3 },
];

export function AiConfigurationPage(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const pathTab = location.pathname.split('/').pop() as TabId | undefined;
  const [activeTab, setActiveTab] = useState<TabId>(
    pathTab && TABS.some((t) => t.id === pathTab) ? pathTab : 'provider'
  );

  useEffect(() => {
    if (pathTab && TABS.some((t) => t.id === pathTab)) {
      setActiveTab(pathTab);
    }
  }, [pathTab]);

  useEffect(() => {
    if (location.pathname === '/settings/ai' || location.pathname.endsWith('/ai')) {
      navigate('/settings/ai/provider', { replace: true });
    }
  }, [location.pathname, navigate]);

  const tabRefs = useRef<Record<TabId, HTMLButtonElement | null>>({
    provider: null,
    privacy: null,
    usage: null,
  });

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    navigate(`/settings/ai/${tab}`);
    requestAnimationFrame(() => {
      tabRefs.current[tab]?.focus();
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI Configuration"
        description="Configure AI features for smart financial insights and automation. Using your own agent of choice that will provide you with full controls."
        helpSlug="settings-ai"
        headingLevel="h2"
      />

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav
          className="flex flex-wrap gap-4"
          role="tablist"
          aria-label="AI Configuration"
        >
          {TABS.map((tab, index) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                ref={(el) => {
                  tabRefs.current[tab.id] = el;
                }}
                key={tab.id}
                type="button"
                role="tab"
                id={`tab-${tab.id}`}
                aria-selected={isActive}
                aria-controls={`panel-${tab.id}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => handleTabChange(tab.id)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    const prev = index > 0 ? TABS[index - 1] : TABS[TABS.length - 1];
                    handleTabChange(prev.id);
                  } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    const next = index < TABS.length - 1 ? TABS[index + 1] : TABS[0];
                    handleTabChange(next.id);
                  }
                }}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  isActive
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="w-5 h-5" aria-hidden="true" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      <div
        id="panel-provider"
        role="tabpanel"
        aria-labelledby="tab-provider"
        hidden={activeTab !== 'provider'}
      >
        {activeTab === 'provider' && <ProviderTab />}
      </div>
      <div
        id="panel-privacy"
        role="tabpanel"
        aria-labelledby="tab-privacy"
        hidden={activeTab !== 'privacy'}
      >
        {activeTab === 'privacy' && <PrivacyControlsTab />}
      </div>
      <div
        id="panel-usage"
        role="tabpanel"
        aria-labelledby="tab-usage"
        hidden={activeTab !== 'usage'}
      >
        {activeTab === 'usage' && <UsageAnalyticsTab />}
      </div>
    </div>
  );
}
